import { FC, useState } from "react";

import { Autocomplete, Paper, Stack, TextField, autocompleteClasses, styled } from "@mui/material";
import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CREDENTIAL_TYPES, CREDENTIAL_TYPES_BY_NAME } from "../KeyEditor";
import { CredentialTypesProps } from "./ControlTypes";

/***
 * Composed Controls  - Built from simpler controls or involve GT 1 field.
 */

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
  backgroundColor: theme.palette.surfaceLight20.main,
  borderRadius: "4px",
})) as typeof TextField;

const CustomPaper = styled(Paper)(({ theme }) => ({
  [`& .${autocompleteClasses.noOptions}`]: {
    backgroundColor: theme.palette.surfaceLight10.main,
    marginTop: "8px",
  },
  [`& .${autocompleteClasses.listbox}`]: {
    borderRadius: "4px",
    [`& .${autocompleteClasses.option}`]: {
      backgroundColor: theme.palette.surfaceLight20.main,
      "&.Mui-focused": {
        backgroundColor: theme.palette.surfaceLight30.main,
      },
    },
  },
})) as typeof Paper;

const labelOf = (key: string) => CREDENTIAL_TYPES_BY_NAME[key]?.display_name ?? key;

const OPTIONS = Object.keys(CREDENTIAL_TYPES)
  .filter((key) => !CREDENTIAL_TYPES_BY_NAME[key]?.deprecated)
  .sort((s1, s2) => labelOf(s1).toLowerCase().localeCompare(labelOf(s2).toLowerCase()))
  .map((key) => ({ label: labelOf(key), value: key }));

const CredentialTypes: FC<CredentialTypesProps> = ({ selected, onChange }) => {
  const [inputValue, setInputValue] = useState("");

  const selectedOption = OPTIONS.find((option) => option.value === selected) ?? null;

  return (
    <Stack
      sx={{ width: "500px", flexDirection: "row", height: "fit-content" }}
      data-testid="credential-types-container"
    >
      <Autocomplete
        disablePortal
        id="credentialTypes"
        options={OPTIONS}
        onInputChange={(_, value) => setInputValue(value)}
        renderInput={(params) => (
          <TextFieldStyled
            data-testid="credential-types"
            {...params}
            label={selected || inputValue ? undefined : "Select credential type"}
            autoFocus={true}
            sx={{
              "& .MuiInputLabel-shrink": { transform: "translate(14px, 16px) scale(1)" },
              "& legend": { maxWidth: "0.01px" },
            }}
          />
        )}
        value={selectedOption}
        onChange={(_, val) => onChange(val?.value ?? "")}
        slots={{ paper: CustomPaper }}
        sx={{ width: "100%", height: "360px" }}
        popupIcon={<CaretDownIcon size={24} />}
      />
    </Stack>
  );
};

export { CredentialTypes };
